// Plan 20-06 — META-06 web-side mirror of the backend LEVEL_REWARD_SCHEDULE.
//
// Single-source-of-truth note (mirrors achievement-meta.ts): the backend
// module (apps/backend/src/economy/level-rewards-schedule.ts) is the runtime
// contract for level→cosmetic UPSERT; this web module is the render-side
// mirror used by InventoryGrid + LevelUpModal. The id+kind+i18n_key set MUST
// match the backend 1:1 — `cosmetic-meta.test.ts` reads the backend source
// as text and asserts the id sets are equal.
//
// We mirror level→cosmetic for completeness (so LevelUpModal can opt to
// surface the unlock level alongside the cosmetic name), kind for variant
// styling, and a `preview_color` hint for the chip rendering.

import { cosmeticPalette } from '../../styles/tokens'

export type CosmeticKind =
  | 'theme_unlock'
  | 'profile_border'
  | 'badge'
  | 'name_color'
  | 'win_screen_flourish'
  | 'push_sound_pack'

export interface CosmeticMeta {
  /** snake_case id, matches backend LEVEL_REWARD_SCHEDULE[*].cosmetic_id. */
  readonly id: string
  /** Level at which the reward unlocks (matches backend `level`). */
  readonly level: number
  readonly kind: CosmeticKind
  /** i18n key for display name. Always `level_rewards.<id>.name`. */
  readonly i18n_key_name: string
  /**
   * CSS color string for the preview swatch — used by InventoryGrid's
   * chip border. Real cosmetic art is operator/backlog (CONTEXT.md §Real
   * cosmetic art deferred); these are geometric placeholders.
   */
  readonly preview_color: string
}

/**
 * The 15 web-side cosmetic render-meta entries. Mirrors the backend
 * LEVEL_REWARD_SCHEDULE 1:1 in id + kind + i18n_key_name (test asserts).
 *
 * `preview_color` choices map each cosmetic to a Phase 17 design-token
 * value where possible; theme-unlock cosmetics reuse the theme accent
 * variable (--color-*-accent style placeholder), badges use metal/gem
 * shorthand colors, borders use the surface-stroke contrast color, and
 * the legend name_color uses the gold accent.
 */
export const COSMETICS_META: ReadonlyArray<CosmeticMeta> = [
  {
    id: 'border_court_of_runes',
    level: 2,
    kind: 'profile_border',
    i18n_key_name: 'level_rewards.border_court_of_runes.name',
    preview_color: cosmeticPalette.amethyst,
  },
  {
    id: 'badge_bronze',
    level: 3,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_bronze.name',
    preview_color: cosmeticPalette.bronze,
  },
  {
    id: 'theme_emerald_grove',
    level: 5,
    kind: 'theme_unlock',
    i18n_key_name: 'level_rewards.theme_emerald_grove.name',
    preview_color: cosmeticPalette.emerald,
  },
  {
    id: 'badge_silver',
    level: 7,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_silver.name',
    preview_color: cosmeticPalette.silver,
  },
  {
    id: 'theme_midnight_sea',
    level: 10,
    kind: 'theme_unlock',
    i18n_key_name: 'level_rewards.theme_midnight_sea.name',
    preview_color: cosmeticPalette.midnightBlue,
  },
  {
    id: 'badge_gold',
    level: 10,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_gold.name',
    preview_color: cosmeticPalette.gold,
  },
  {
    id: 'badge_sapphire',
    level: 13,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_sapphire.name',
    preview_color: cosmeticPalette.sapphire,
  },
  {
    id: 'theme_velvet_noir',
    level: 15,
    kind: 'theme_unlock',
    i18n_key_name: 'level_rewards.theme_velvet_noir.name',
    preview_color: cosmeticPalette.velvetNoir,
  },
  {
    id: 'badge_ruby',
    level: 18,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_ruby.name',
    preview_color: cosmeticPalette.ruby,
  },
  {
    id: 'border_veteran',
    level: 20,
    kind: 'profile_border',
    i18n_key_name: 'level_rewards.border_veteran.name',
    preview_color: cosmeticPalette.copper,
  },
  {
    id: 'badge_emerald',
    level: 23,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_emerald.name',
    preview_color: cosmeticPalette.emerald,
  },
  {
    id: 'border_master',
    level: 25,
    kind: 'profile_border',
    i18n_key_name: 'level_rewards.border_master.name',
    preview_color: cosmeticPalette.silver,
  },
  {
    id: 'badge_diamond',
    level: 28,
    kind: 'badge',
    i18n_key_name: 'level_rewards.badge_diamond.name',
    preview_color: cosmeticPalette.diamond,
  },
  {
    id: 'border_legend',
    level: 30,
    kind: 'profile_border',
    i18n_key_name: 'level_rewards.border_legend.name',
    preview_color: cosmeticPalette.gold,
  },
  {
    id: 'name_color_legend_gold',
    level: 30,
    kind: 'name_color',
    i18n_key_name: 'level_rewards.name_color_legend_gold.name',
    preview_color: cosmeticPalette.gold,
  },
] as const

/**
 * O(1) lookup map. Consumed by `InventoryGrid` + `LevelUpModal` when
 * projecting backend cosmetic_id strings into renderable meta. Unknown
 * ids resolve to `undefined` — the grid skips them safely.
 */
export const COSMETICS_BY_ID: ReadonlyMap<string, CosmeticMeta> = new Map(
  COSMETICS_META.map((c) => [c.id, c]),
)
